/**
 * HeroCover - Premium Hero Banner with Integrated Profile
 * 
 * Design:
 * - Tall immersive banner with cinematic gradient
 * - Profile image INSIDE the banner (floating glass card)
 * - Restaurant name + meta as white overlay text on the banner
 * - Parallax-like depth with layered gradients
 * - Back/Favorite glass-morphism buttons
 */

import React from 'react';
import { ChevronLeft, Heart, Star, UtensilsCrossed } from 'lucide-react';
import { normalizeImagePath } from '@/lib/utils';

interface HeroCoverProps {
  restaurantName: string;
  heroImage: string | null;
  profileImage?: string | null;
  rating?: number | null;
  ratingCount?: number | null;
  isOpen: boolean;
  openStatusText: string;
  isFavorite: boolean;
  onBack: () => void;
  onToggleFavorite: () => void;
}

export const HeroCover: React.FC<HeroCoverProps> = ({
  restaurantName,
  heroImage,
  profileImage,
  rating,
  ratingCount,
  isOpen,
  openStatusText,
  isFavorite,
  onBack,
  onToggleFavorite,
}) => {
  const displayRating = typeof rating === 'number' ? rating.toFixed(1) : null;
  const displayCount = typeof ratingCount === 'number' ? ratingCount : 0;
  
  // Profile image: explicit profileImage prop, fallback to heroImage
  const profileSrc = profileImage || heroImage;

  return (
    <header className="relative w-full pb-4">
      {/* Outer wrapper — sets the height, the inner clipped div does the rounding */}
      <div 
        className="relative w-full overflow-hidden"
        style={{ 
          height: 'clamp(320px, 45vw, 440px)',
          borderRadius: '0 0 36px 36px',
        }}
      >
        {/* Cover Image or Premium Placeholder */}
        {heroImage ? (
          <div 
            className="absolute inset-0 bg-cover bg-center protected-image"
            style={{ 
              backgroundImage: `url(${normalizeImagePath(heroImage)})`,
              transform: 'scale(1.02)',
            }}
            role="img"
            aria-label={restaurantName}
          />
        ) : (
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(135deg, #0a5c3a 0%, #0E7F4F 30%, #18B66A 60%, #34d891 100%)',
            }}
          >
            <div 
              className="absolute inset-0 opacity-10"
              style={{
                backgroundImage: `radial-gradient(circle at 20% 50%, rgba(255,255,255,0.3) 0%, transparent 50%),
                  radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 40%),
                  radial-gradient(circle at 60% 80%, rgba(255,255,255,0.15) 0%, transparent 45%)`,
              }}
            />
          </div>
        )}
        
        {/* Cinematic gradient overlay */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.45) 30%, rgba(0,0,0,0.12) 55%, transparent 75%),
              linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, transparent 20%)
            `,
          }}
        />
        
        {/* Navigation Buttons */}
        <div 
          className="absolute top-0 left-0 right-0 flex justify-between items-center px-4 z-20"
          style={{ paddingTop: 'max(16px, env(safe-area-inset-top, 0px))' }}
        >
          <button
            onClick={onBack}
            className="w-11 h-11 rounded-full flex items-center justify-center transition-colors active:scale-95"
            style={{
              background: 'rgba(255,255,255,0.18)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.25)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            }}
            aria-label="Zurück"
          >
            <ChevronLeft className="h-6 w-6 text-white" strokeWidth={2.5} />
          </button>
          
          <button
            onClick={onToggleFavorite}
            className="w-11 h-11 rounded-full flex items-center justify-center transition-colors active:scale-95"
            style={{
              background: isFavorite ? 'rgba(239,68,68,0.9)' : 'rgba(255,255,255,0.18)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: `1px solid ${isFavorite ? 'rgba(239,68,68,0.4)' : 'rgba(255,255,255,0.25)'}`,
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            }}
            aria-label={isFavorite ? 'Von Favoriten entfernen' : 'Zu Favoriten hinzufügen'}
          >
            <Heart 
              className={`h-5 w-5 ${isFavorite ? 'text-white fill-white' : 'text-white'}`} 
            />
          </button>
        </div>
        
        {/* Restaurant Info Overlay — inside rounded container, at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 z-10 px-5 pb-7 lg:pb-9">
          <div className="flex items-end gap-4" style={{ maxWidth: '720px' }}>
            {/* Profile Image */}
            <div 
              className="shrink-0 protected-image"
              style={{
                width: '96px',
                height: '96px',
                borderRadius: '24px',
                border: '3.5px solid rgba(255,255,255,0.7)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.15)',
                overflow: 'hidden',
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
              }}
            >
              {profileSrc ? (
                <div 
                  className="w-full h-full bg-cover bg-center"
                  style={{ backgroundImage: `url(${normalizeImagePath(profileSrc)})` }}
                  role="img"
                  aria-label={`${restaurantName} Profilbild`}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <UtensilsCrossed className="w-8 h-8 text-white/50" />
                </div>
              )}
            </div>
            
            {/* Name + Meta */}
            <div className="flex-1 min-w-0 pb-0.5">
              <h1 
                className="text-white font-bold leading-tight"
                style={{ 
                  fontSize: 'clamp(1.35rem, 4vw, 2rem)',
                  textShadow: '0 2px 12px rgba(0,0,0,0.4)',
                }}
              >
                {restaurantName}
              </h1>
              
              <div className="flex items-center gap-2.5 mt-1.5 flex-wrap">
                {displayRating && (
                  <div className="flex items-center gap-1">
                    <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
                    <span className="text-white text-sm font-semibold" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.3)' }}>{displayRating}</span>
                    {displayCount > 0 && (
                      <span className="text-white/60 text-xs">({displayCount})</span>
                    )}
                  </div>
                )}
                
                {displayRating && <span className="text-white/30">·</span>}
                
                <div className="flex items-center gap-1.5">
                  <div 
                    className="w-2 h-2 rounded-full"
                    style={{
                      background: isOpen ? '#34d399' : '#f87171',
                      boxShadow: isOpen 
                        ? '0 0 8px rgba(52,211,153,0.6)' 
                        : '0 0 8px rgba(248,113,113,0.6)',
                    }}
                  />
                  <span 
                    className="text-xs font-medium"
                    style={{ 
                      color: isOpen ? '#a7f3d0' : '#fca5a5',
                      textShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    }}
                  >
                    {openStatusText}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Soft shadow glow below the rounded banner */}
      <div 
        className="absolute left-8 right-8 pointer-events-none"
        style={{
          bottom: '0px',
          height: '20px',
          background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.06) 0%, transparent 70%)',
          filter: 'blur(6px)',
        }}
      />
    </header>
  );
};

export default HeroCover;
